import { InputHTMLAttributes, forwardRef, ReactNode } from 'react';
import './Form.css';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** Label text */
  label?: ReactNode;
  /** Error state */
  error?: boolean;
}

/**
 * Checkbox component for boolean selection.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Accept terms and conditions"
 *   checked={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error = false, className = '', id, ...props }, ref) => {
    const checkboxId =
      id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const containerClassNames = [
      'ui-checkbox-container',
      error && 'ui-checkbox-container--error',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={containerClassNames} htmlFor={checkboxId}>
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          className="ui-checkbox"
          {...props}
        />
        <span className="ui-checkbox__checkmark" />
        {label && <span className="ui-checkbox__label">{label}</span>}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
